import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  adminListClusters,
  adminGetClusterDetail,
  adminSetClusterStatus,
  adminSeedStarterClusters,
  adminGenerateClusterBatch,
} from "@/lib/blog-admin.functions";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, CalendarClock, Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/blog-clusters")({
  component: BlogClustersPage,
  head: () => ({ meta: [{ title: "Blog clusters · Admin" }, { name: "robots", content: "noindex" }] }),
});

const STATUSES = ["draft", "active", "paused", "complete"] as const;
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

function BlogClustersPage() {
  const list = useServerFn(adminListClusters);
  const detail = useServerFn(adminGetClusterDetail);
  const setStatus = useServerFn(adminSetClusterStatus);
  const seed = useServerFn(adminSeedStarterClusters);
  const runBatch = useServerFn(adminGenerateClusterBatch);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [batching, setBatching] = useState(false);

  const q = useQuery({ queryKey: ["admin-blog-clusters"], queryFn: () => list() });
  const clusters = Array.isArray(q.data) ? (q.data as any[]) : [];

  const d = useQuery({
    queryKey: ["admin-blog-cluster", selected],
    queryFn: () => detail({ data: { id: selected! } }),
    enabled: !!selected,
  });

  const changeStatus = async (id: string, status: typeof STATUSES[number]) => {
    try {
      await setStatus({ data: { id, status } });
      await qc.invalidateQueries({ queryKey: ["admin-blog-clusters"] });
      toast.success("Cluster updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Update failed");
    }
  };

  const runSeed = async () => {
    setSeeding(true);
    try {
      const r = (await seed({})) as any;
      const made = (r?.results ?? []).filter((x: any) => x.created).length;
      toast.success(`Seeded ${made} new cluster(s)`);
      await qc.invalidateQueries({ queryKey: ["admin-blog-clusters"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const runWeekly = async (id: string) => {
    if (!confirm("Generate up to 7 real, unique AI drafts for this cluster and schedule them Mon-Sun? This may take a few minutes.")) return;
    setBatching(true);
    try {
      const r = (await runBatch({ data: { clusterId: id, generateHero: true } })) as any;
      toast.success(`Drafted ${r?.created?.length ?? 0} post(s), skipped ${r?.skipped?.length ?? 0}`);
      await qc.invalidateQueries({ queryKey: ["admin-blog-cluster", id] });
      await qc.invalidateQueries({ queryKey: ["admin-posts"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Batch failed");
    } finally {
      setBatching(false);
    }
  };

  return (
    <div>
      <BlogSubNav />
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4">
        <div>
          <p className="font-display text-sm font-bold uppercase tracking-widest">Sunday cluster workflow</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Each cluster = 1 pillar (publishes Monday) + 6 supporting spokes (Tue–Sun). All drafts are original, factual, and compliance-checked.
          </p>
        </div>
        <Button onClick={runSeed} disabled={seeding} variant="outline">
          {seeding ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
          Seed starter clusters
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <div>
          <h1 className="font-display text-2xl font-bold">Topic clusters</h1>
          <p className="mt-1 text-sm text-muted-foreground">Pillar-and-spoke groupings that guide the AI blog agent.</p>
          <div className="mt-6 space-y-3">
            {q.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : clusters.length === 0 ? (
              <p className="text-sm text-muted-foreground">No clusters yet. Click <b>Seed starter clusters</b> above to create three real investor-lending clusters.</p>
            ) : clusters.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelected(c.id)}
                className={`w-full rounded-2xl border p-4 text-left transition ${
                  selected === c.id ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{c.name ?? "Untitled cluster"}</p>
                    <p className="text-xs text-muted-foreground">{c.service_page ?? c.notes ?? ""}</p>
                  </div>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase">{c.status}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <aside className="rounded-2xl border border-border bg-card p-5">
          {!selected ? (
            <p className="text-sm text-muted-foreground">Select a cluster to see its pillar + supporting topics.</p>
          ) : d.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : d.data ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Cluster</p>
                  <h2 className="mt-1 font-display text-lg font-bold">{(d.data as any).cluster.name}</h2>
                  {(d.data as any).cluster.notes && (
                    <p className="mt-1 text-xs text-muted-foreground">{(d.data as any).cluster.notes}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {STATUSES.map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={(d.data as any).cluster.status === s ? "default" : "outline"}
                      onClick={() => changeStatus((d.data as any).cluster.id, s)}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>

              <Button
                className="mt-4 w-full"
                onClick={() => runWeekly((d.data as any).cluster.id)}
                disabled={batching}
              >
                {batching ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CalendarClock className="mr-1 h-4 w-4" />}
                Generate this week's batch (pillar + 6 supporting)
              </Button>

              <div className="mt-5 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Planned topics</p>
                {((d.data as any).topics as any[]).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No topics yet.</p>
                ) : (
                  ((d.data as any).topics as any[]).map((t, idx) => {
                    const isPillar = t.role === "pillar";
                    const dayIdx = isPillar ? 0 : Math.min(idx, 6);
                    const day = DAY_NAMES[dayIdx];
                    const drafted = !!t.post_id;
                    return (
                      <div
                        key={t.id}
                        className={`rounded-lg border p-3 ${isPillar ? "border-primary/40 bg-primary/5" : "border-border"}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${isPillar ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                                {isPillar && <Star className="h-3 w-3" />}
                                {t.role}
                              </span>
                              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
                                Publishes {day}
                              </span>
                              {drafted && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-600">
                                  <CheckCircle2 className="h-3 w-3" /> Drafted
                                </span>
                              )}
                            </div>
                            <p className="mt-1.5 text-sm font-medium">{t.planned_title}</p>
                            {t.planned_slug && (
                              <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">/blog/{t.planned_slug}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
